import clsx from 'clsx'
import { ArrowRight } from 'lucide-react'
import React, { useMemo } from 'react'

import { membersTransactions } from 'rails_routes'
import { TextLink } from 'slab'
import { conditionallyLocalizeDate, formatMoney } from 'utils'

import { type MemberBalanceType } from '../../types'

interface CanUse {
  byDate: Date;
  forYear: number;
}

type MemberBalanceCardProps =
  | {
    balanceType: 'available';
    isPriorYear: true;
    effectiveDate: Date;
    amount: number;
    canUse: CanUse;
  }
  | {
    balanceType: MemberBalanceType;
    isPriorYear: boolean;
    effectiveDate: Date;
    amount: number;
    canUse?: CanUse;
  }

const healthcareExpensesLink = <TextLink href='https://support.thatch.com/qualified-medical-expense' target='_blank'>healthcare expenses</TextLink>

const MemberBalanceCard: React.FC<MemberBalanceCardProps> = ({ balanceType, isPriorYear, amount, canUse, effectiveDate }) => {
  const { description, caption, isForFuture } = useMemo(() => {
    if (balanceType === 'available') {
      if (new Date() < effectiveDate) {
        const date = conditionallyLocalizeDate(effectiveDate)

        return {
          description: 'Available balance',
          caption: <>We'll start adding money to your balance on {date}</>,
          isForFuture: true,
        }
      } else if (isPriorYear && canUse) {
        const date = conditionallyLocalizeDate(canUse.byDate)

        return {
          description: 'Available balance',
          caption: <>You have until {date} to use this on {healthcareExpensesLink} that happened in {canUse.forYear}</>,
        }
      } else {
        return {
          description: 'Available balance',
          caption: <>Use your balance on {healthcareExpensesLink} like copays, prescriptions, and the Thatch Marketplace</>,
        }
      }
    }

    if (balanceType === 'unused') {
      return {
        description: 'Unused balance',
        caption: <>Balances don't roll over</>
      }
    }

    // deduction balance type
    return {
      description: isPriorYear ? 'You paid' : 'You\'re paying',
      caption: <>Collected from your paychecks to cover the remaining cost of your insurance</>
    }
  }, [balanceType, amount, canUse, effectiveDate])

  const backgroundColor = balanceType === 'unused' || (balanceType === 'deduction' && isPriorYear) ? 'bg-gray-50' : 'bg-white'
  const isOnTransactionsPage = window.location.pathname === membersTransactions.index()
  const showTransactionsButton = balanceType !== 'deduction' && !isOnTransactionsPage

  const formattedAmount = formatMoney(amount)
  const [dollarsWithSign, cents] = formattedAmount.split('.')
  const dollars = dollarsWithSign.replace('$', '')
  const showPerMonth = balanceType === 'deduction'

  return (
    <div className={`rounded-2xl border border-gray-200 shadow-sm ${backgroundColor} h-full`}>
      <div className='flex flex-col flex-grow justify-between p-4'>
        <div className='flex flex-row justify-between items-start'>
          <div className='flex flex-col gap-1'>
            <span className='body-sm text-gray-500'>{description}</span>
            <div className={clsx('flex flex-row', {
              'items-center': isForFuture,
              'items-start': !isForFuture,
            })}
            >
              {isForFuture
                ? (
                  <>
                    <span className='body-sm-bold text-main'>$</span>
                    <span className='heading-lg text-main ml-2'>--</span>
                  </>)
                : (
                  <>
                    <span className='body-sm-bold text-main mt-1'>$</span>
                    <span className='heading-lg text-main ml-1'>{dollars}</span>
                    <span className='body-sm-bold text-main mt-1'>.{cents}</span>
                  </>)}
              {showPerMonth && <span className='body-sm text-main ml-1 mt-1'>/mo</span>}
            </div>
          </div>
          {showTransactionsButton && (
            <div className='flex items-center'>
              <a href={membersTransactions.index()} className='relative group'>
                <svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32' className='text-gray-100 group-hover:text-gray-200 transition-colors'>
                  <circle cx='16' cy='16' r='16' fill='currentColor' />
                </svg>
                <ArrowRight className='absolute inset-0 m-auto w-4 h-4 text-gray-500 group-hover:text-gray-600 transition-colors' />
              </a>
            </div>
          )}
        </div>
        <div className='caption text-main mt-2'>
          {caption}
        </div>
      </div>
    </div>
  )
}

export default MemberBalanceCard
